import { useState, useRef, useCallback, useEffect } from 'react'
import { transcriptionAPI } from '../services/api'
import { useI18n } from '../i18n'
import { startTranscribeStream } from '../lib/transcribeStream'
import {
  micFailureMessage,
} from '../components/voice/transcriptionOutcome'
import { isNativeApp } from '../utils/authStorage'

/**
 * useVoiceInput v242.56.0
 *
 * Live streaming voice-to-text. Words appear in the input as the user speaks.
 *
 * Primary: Amazon Transcribe streaming over a presigned WebSocket (AWS BAA).
 * There is no fallback: Transcribe streaming is the only engine.
 *
 * v242.56.0 (AI-4): the Web Speech API path is REMOVED. It shipped patient
 * audio to Google with no BAA on desktop Chrome/Edge, and on iOS WKWebView it
 * exposed webkitSpeechRecognition but then failed with `service-not-allowed`
 * — so voice input on iOS was broken, not merely degraded. The earlier scope
 * note claiming WKWebView has no Web Speech API was incorrect.
 * 
 * Usage:
 *   const voice = useVoiceInput({
 *     onTranscript: (text) => setInput(text),       // final committed text
 *     onInterim: (text) => setInterimText(text),     // live preview (optional)
 *     language: 'en',
 *   })
 *   
 *   <button onClick={voice.toggle}>
 *     {voice.isListening ? <MicOff /> : <Mic />}
 *   </button>
 *   {voice.isListening && <span>{voice.interim}</span>}
 */

// Language code mapping for Web Speech API (BCP 47)
const LANG_MAP = {
  en: 'en-US', es: 'es-ES', ar: 'ar-SA', ru: 'ru-RU',
  zh: 'zh-CN', vi: 'vi-VN', ko: 'ko-KR', fr: 'fr-FR',
  pt: 'pt-BR', hi: 'hi-IN', tl: 'fil-PH',
}

// Transcribe streaming needs getUserMedia + WebSocket. There is nothing to fall
// back TO any more (WHISPER-REMOVAL-1), so a browser that fails this has no
// voice input at all and the caller renders its text field instead.
const canStream = () => (
  typeof window !== 'undefined' &&
  typeof window.WebSocket !== 'undefined' &&
  !!navigator?.mediaDevices?.getUserMedia
)

export default function useVoiceInput({ onTranscript, onInterim, language = 'en', maxDuration = 60 } = {}) {
  const { t } = useI18n()
  const [isListening, setIsListening] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [interim, setInterim] = useState('')
  const [error, setError] = useState(null)
  // 'transcribe' once the browser is known capable, null before that and on a
  // browser that cannot stream. It no longer SELECTS anything — there is one
  // engine — but callers read it for a label, so it keeps its shape.
  const [mode, setMode] = useState(null)
  const streamSessionRef = useRef(null)

  const accumulatedRef = useRef('') // accumulates final results across recognition events
  const timeoutRef = useRef(null)

  // Detect available mode on mount
  useEffect(() => {
    setMode(canStream() ? 'transcribe' : null)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening()
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])


  // Track isListening in a ref for the onend handler
  const isListeningRef = useRef(false)
  useEffect(() => {
    isListeningRef.current = isListening
  }, [isListening])

  // ═══════════════════════════════════════
  // The batch record-and-transcribe leg was REMOVED (WHISPER-REMOVAL-1).
  // It captured to a MediaRecorder, base64-ed the blob and posted it to the
  // batch endpoint. OPENAI_API_KEY has been absent from every secret group
  // since D48b, so that post could only ever answer with the configuration
  // envelope — this hook powers the home screen and the Orella chat, so that
  // envelope could surface in either. Streaming is the only engine now.
  // ═══════════════════════════════════════

  // ═══════════════════════════════════════
  // CONTROLS
  // ═══════════════════════════════════════
  // ─── Transcribe streaming (primary) ───
  const startTranscribeStreaming = useCallback(async () => {
    setError(null)
    setInterim('')
    accumulatedRef.current = ''

    try {
      const session = await startTranscribeStream({
        mint: () => transcriptionAPI.streamUrl({ surface: 'patient', language }),
        onPartial: (text) => {
          setInterim(text)
          if (onInterim) onInterim(text)
        },
        onCommit: (text) => {
          accumulatedRef.current = (accumulatedRef.current + ' ' + text).trim()
          setInterim('')
          if (onInterim) onInterim('')
          if (onTranscript) onTranscript(accumulatedRef.current)
        },
        onError: (err) => console.warn('[useVoiceInput] stream error:', err.name, err.message),
        onClose: () => setIsListening(false),
      })

      streamSessionRef.current = session
      setIsListening(true)

      if (maxDuration) {
        timeoutRef.current = setTimeout(() => { stopListeningRef.current?.() }, maxDuration * 1000)
      }
    } catch (err) {
      // ★★★ v242.172.0 · APL-3 — THIS CATCH USED TO SAY "your recording is saved".
      //   Nothing reaches here with a recording: `startTranscribeStream` opens its own
      //   capture on this path, so a throw means getUserMedia never handed over audio.
      //   The old sentence also said "record again", which for a DENIED microphone is
      //   advice that cannot work — the next tap re-throws the same denial until the
      //   permission itself changes. Two false clauses in the one sentence a patient
      //   reads when the mic fails, on Home and in the chat.
      //   ★★ The namespace was wrong too: this hook is not DescribeVoice, and its
      //   messages were being looked up under `describeVoice.*`.
      // No batch leg to fall back to any more. Say so; the caller decides what
      // to render, and every caller of this hook has a text input beside it.
      setError(micFailureMessage(err, t, 'voiceInput', isNativeApp()))
      setIsListening(false)
    }
  }, [language, maxDuration, onInterim, onTranscript, t])

  const stopListeningRef = useRef(null)

  const stopListening = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // Stop the Transcribe stream
    if (streamSessionRef.current) {
      const session = streamSessionRef.current
      streamSessionRef.current = null
      try { session.stop() } catch {}
    }

    // The MediaRecorder and its capture were the batch leg's; both are gone
    // (WHISPER-REMOVAL-1). startTranscribeStream owns the microphone now and
    // releases it in stop().

    setIsListening(false)
  }, [])

  useEffect(() => { stopListeningRef.current = stopListening }, [stopListening])

  const startListening = useCallback(() => {
    if (isListening || isTranscribing) return

    // `mode` used to choose between streaming and the batch leg. There is one
    // engine now, so it selects nothing (WHISPER-REMOVAL-1).
    startTranscribeStreaming()
  }, [isListening, isTranscribing, startTranscribeStreaming])

  const toggle = useCallback(() => {
    if (isListening) {
      stopListening()
    } else if (!isTranscribing) {
      startListening()
    }
  }, [isListening, isTranscribing, startListening, stopListening])

  const clearError = useCallback(() => setError(null), [])

  return {
    isListening,
    isTranscribing,
    interim,
    error,
    mode,         // 'transcribe' | null — for UI hints
    isAvailable: mode !== null,
    toggle,
    startListening,
    stopListening,
    clearError,
    // Legacy aliases for backward compat with v169.2.0 callers
    isRecording: isListening,
    toggleRecording: toggle,
  }
}
